import { useCallback, useRef } from 'react';

import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import { mapAdapterEntriesToServiceEntries } from 'features/promotions/utils/map-best-promotion-input';

import {
  ILoyaltyPromotionInput,
  ILoyaltyServiceOffersQuery,
  ILoyaltyServiceOffersQueryVariables,
  LoyaltyServiceOfferRedemptionType,
  LoyaltyServiceOffersDocument,
  LoyaltyServicePromotionType,
} from 'generated/graphql-gateway';
import { actions, useAppDispatch } from 'state/global-state';
import {
  ICartEntryAdapter,
  IIncentiveEvaluationResult,
  IncentiveEvaluationMap,
} from 'state/loyalty/hooks/types';
import { logger } from 'utils/logger';

import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

type LoyaltyServiceOffersItems = NonNullable<
  ILoyaltyServiceOffersQuery['loyaltyServiceOffers']
>['items'];

/**
 * Builds the map the cart reads to show what's wrong with an applied offer
 * (e.g. "Add fries to unlock this offer"). Loyalty-service version of the older
 * `createFeedbackMaps`.
 *
 * Keyed by `loyaltyEngineId` because that's how the cart looks an offer up.
 *
 * Offers missing from the response get no entry on purpose: they were rejected
 * for a reason the user can't fix and a separate step already removes them, so a
 * synthesized message here would be redundant and could wrongly block checkout.
 *
 * Problem codes already match what the cart expects, so nothing needs translating.
 */
export const createServiceOfferFeedbackMap = (
  items?: LoyaltyServiceOffersItems | null
): IncentiveEvaluationMap => {
  const feedbackMap: IncentiveEvaluationMap = {};

  for (const item of items ?? []) {
    // We only ask for individual offers, not offer groups. Skip anything that
    // isn't a single offer so a group id can never end up as a key.
    if (item.__typename !== 'LoyaltyServiceOffer') {
      continue;
    }

    const key = item.loyaltyEngineId ?? item.id;
    if (!key) {
      continue;
    }

    feedbackMap[key] = (item.errors ?? []) as IIncentiveEvaluationResult[];
  }

  return feedbackMap;
};

export interface EvaluateLoyaltyServiceOffersParams {
  /** Offers the user has applied to their cart. */
  appliedIncentives: ILoyaltyPromotionInput[];
  /** The items currently in the cart. */
  cartEntries?: ICartEntryAdapter[];
  serviceMode?: string | null;
  storeId?: string | null;
}

/**
 * Checks the offers a user has applied to their cart against the loyalty service
 * and returns what (if anything) is wrong with each one, in the shape the cart
 * already knows how to display. This is the loyalty-service version of the older
 * `evaluateLoyaltyOffers`.
 *
 * Only meant for signed-in users: the caller skips it when there is no loyalty
 * user, and for signed-out users the loyalty service hides invalid offers, so
 * there is nothing to report.
 */
export const useEvaluateLoyaltyServiceOffers = () => {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  // Guards against out-of-order service responses clobbering a newer feedback map.
  const latestEvaluationRef = useRef(0);

  const evaluateLoyaltyServiceOffers = useCallback(
    async ({
      appliedIncentives,
      cartEntries,
      serviceMode,
      storeId,
    }: EvaluateLoyaltyServiceOffersParams): Promise<IncentiveEvaluationMap | undefined> => {
      // Drop blank ids: the service UUID-validates the `ids` batch and rejects
      // blanks, failing the whole query. Matches the `getAppliedOfferIds` guard.
      const serviceAppliedIncentives = appliedIncentives
        .filter(({ id }) => id)
        .map(({ id, lineId }) => ({
          id,
          lineId,
          type: LoyaltyServicePromotionType.OFFER,
        }));

      // Return undefined so existing feedback isn't wiped by an empty response.
      if (serviceAppliedIncentives.length === 0) {
        return undefined;
      }

      try {
        const { data }: ApolloQueryResult<ILoyaltyServiceOffersQuery> = await client.query<
          ILoyaltyServiceOffersQuery,
          ILoyaltyServiceOffersQueryVariables
        >({
          query: LoyaltyServiceOffersDocument,
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
          variables: {
            ids: serviceAppliedIncentives.map(({ id }) => id),
            appliedIncentives: serviceAppliedIncentives,
            cartEntries: mapAdapterEntriesToServiceEntries(cartEntries ?? []),
            serviceMode: getLoyaltyServiceServiceMode(serviceMode),
            storeId: storeId ?? undefined,
            redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
            omitInvalids: false,
            includeOfferGroups: false,
          },
        });

        const items = data?.loyaltyServiceOffers?.items;
        // If the request comes back with nothing (e.g. it failed), leave the
        // cart's current messages as-is instead of clearing them.
        if (!items) {
          return undefined;
        }

        return createServiceOfferFeedbackMap(items);
      } catch (e) {
        logger.error(`Loyalty service offers evaluation error: ${e}`);
        throw e;
      }
    },
    [client]
  );

  /**
   * Evaluates the applied offers and applies the result to the cart's feedback
   * map, guarding against out-of-order responses. This is the shared result
   * handling all cart seams previously duplicated (stale-guard + dispatch).
   *
   * An `undefined` result (blank ids / no items / failed request) intentionally
   * leaves the current feedback untouched instead of clearing it.
   */
  const evaluateAndApplyOffersFeedback = useCallback(
    async (params: EvaluateLoyaltyServiceOffersParams): Promise<void> => {
      const evaluationId = ++latestEvaluationRef.current;
      const result = await evaluateLoyaltyServiceOffers(params);
      if (result && evaluationId === latestEvaluationRef.current) {
        dispatch(actions.loyalty.setOffersFeedbackMap(result));
      }
    },
    [evaluateLoyaltyServiceOffers, dispatch]
  );

  return { evaluateLoyaltyServiceOffers, evaluateAndApplyOffersFeedback };
};
